import { useEffect, useMemo, useState } from 'react'
import BackButton from '../../../components/ui-components/BackButton'
import { goBack, onReady, postCTA, trackEvent } from '../../../utils/appBridge'
import { EliteLandingPageState, EliteOnboardingStatusResponse, EventNames } from '../../../types/misc.types'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { KYCCompletionBottomSheet } from '../../../components/BottomSheetComponents/elite-family/KYCCompletionBottomSheet'
import { FamilyFAQs } from './FamilyFAQs'
import { PrimaryButton } from '../../../components/execution/PrimaryButton'
import RiveComponent, { RiveFit } from '../../../components/RiveComponent'
import { useQuery } from '@tanstack/react-query'
import { fetchFamilyEliteDetails } from '../../../repository/misc'
import ThreeLineLoading from '../../../components/ui-components/loading/ThreeLineLoading'
import { SupportText } from './SupportText'
import { familyEliteLandingPageStateConfig } from './familyEliteLandingPageStateConfig'
import RenderHTML from '../../../components/ui-components/RenderHTML'
import { GROUPED_STATIC_TEXT_ROLE } from '../../../components/ui-components/NameValueTableRow'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getBottomPadding, getTopPadding } from '../../../utils/sessionStorage'
import { useSetAppBarTheme } from '../../../hooks/useSetAppBarTheme'
import { getCloseIcon } from '../../../components/elite/ElitePageBackBar'
import { getEliteBuyCtaEventExtras } from '../../../utils/eliteEventExtras'
import { EliteBuyCtaExtrasProvider } from '../../../components/elite/EliteBuyCtaExtrasContext'

export const EliteFamilyLandingPage = () => {
  const [loadPricingRive, setLoadPricingRive] = useState(false)
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')

  useSetAppBarTheme({ appBarTheme: 'dark' })

  const {
    data: familyEliteDetails,
    isLoading,
    isSuccess,
    isError
  } = useQuery({
    queryKey: ['FAMILY_ELITE_DETAILS'],
    queryFn: fetchFamilyEliteDetails,
    retry: 3
  })

  const familyMemberName = useMemo(
    () => familyEliteDetails?.secondaryUserName?.split(' ')?.[0] ?? '',
    [familyEliteDetails]
  )

  const eliteLandingPageState = useMemo(() => {
    return (familyEliteDetails?.eliteLandingPageState ??
      EliteLandingPageState.ONB_NOT_INITIATED) as `${EliteLandingPageState}`
  }, [familyEliteDetails])

  const stateConfig = useMemo(() => {
    return familyEliteLandingPageStateConfig[eliteLandingPageState]
  }, [eliteLandingPageState])

  const handleBack = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ...getEliteBuyCtaEventExtras(familyEliteDetails as any),
        eventId: 'webview_back',
        screenType: 'Elite_Family_Landing'
      }
    })
    goBack()
  }

  const handleClose = () => {
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ...getEliteBuyCtaEventExtras(familyEliteDetails as any),
        eventId: 'webview_close',
        screenType: 'Elite_Family_Landing'
      }
    })
    goBack()
  }

  const handleCTACall = () => {
    if (familyEliteDetails?.cta) {
      postCTA(JSON.stringify(familyEliteDetails?.cta))
      setTimeout(() => {
        closeBottomSheet()
      }, 250)
    }
  }

  const handleContinue = () => {
    if (familyEliteDetails?.consentText) {
      openBottomSheet(
        <KYCCompletionBottomSheet
          familyMemberName={familyMemberName}
          consentText={familyEliteDetails?.consentText ?? ''}
          onDismiss={closeBottomSheet}
          onAuthorize={() => {
            // Handle OTP authorization
            handleCTACall()
          }}
        />
      )
    } else {
      handleCTACall()
    }
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ...getEliteBuyCtaEventExtras(familyEliteDetails as any),
        screenType: 'Elite_Family_Landing'
      }
    })
  }, [])

  useEffect(() => {
    if (isSuccess || isError) {
      onReady()
    }
  }, [isSuccess, isError])

  if (isLoading) return <ThreeLineLoading />

  return (
    <EliteBuyCtaExtrasProvider status={familyEliteDetails as EliteOnboardingStatusResponse | undefined}>
      <div className='h-screen flex flex-col relative bg-[#FEFFFE] w-full'>
        <div style={{ top: `${getTopPadding() + 20}px` }} className='absolute left-5 z-10'>
          {searchParams.has('from') && !from ? null : from === 'APP_STARTUP' ? (
            <div className='cursor-pointer' onClick={handleClose}>
              <RenderHTML innerHTMLText={getCloseIcon('#000000')} />
            </div>
          ) : (
            <BackButton fill='#000000' size={24} onClick={handleBack} />
          )}
        </div>

        <div className='flex  flex-1 h-1 w-full no-scrollbar overflow-y-auto overflow-x-hidden overscroll-none'>
          <div className='flex flex-col items-center w-full h-max'>
            <div className='w-full h-[250px]'>
              <RiveComponent
                src='/rives/family/bg-hero.riv'
                autoplay
                fit={RiveFit.WIDTH}
                fallback={<EliteFallBackComponent />}
                onError={() => {
                  setLoadPricingRive(true)
                }}
                onStop={() => {
                  setLoadPricingRive(true)
                }}
              />
            </div>

            {loadPricingRive && (
              <div
                style={{ paddingBottom: `${getBottomPadding()}px` }}
                className='flex flex-col pt-5 items-center px-5 w-full animate-fade-150'
              >
                <div className='text-[#4D3E38] text-[16px] font-[500] leading-[120%] text-center'>
                  Get Elite for {familyMemberName} at just
                </div>
                <div className='w-full flex justify-center h-[52px]'>
                  <RiveComponent
                    src='/rives/family/price-cut.riv'
                    autoplay
                    fit={RiveFit.HEIGHT}
                    textRunValues={{
                      Pricing: `₹${familyEliteDetails?.userSubscriptionDetails?.membershipFees?.amount ?? 749}/yr`
                    }}
                    fallback={
                      <PricingComponent
                        price={familyEliteDetails?.userSubscriptionDetails?.membershipFees?.amount ?? 749}
                      />
                    }
                  />
                </div>

                <div className='text-[#6A6E6E] text-[13px] font-[400] leading-[160%] text-center max-w-[300px] pb-8'>
                  A special price, just for your loved ones. Fund selection, review to rebalancing - now make their
                  portfolios strong like yours, with Elite.
                </div>

                <div className='w-[95%] h-[1px] bg-[#EBF2F2] mt-3 opacity-90' />

                <div className='text-[#8E9393] text-[13px] font-[400] mr-auto pl-5 pt-8 pb-3'>FAQs</div>

                <FamilyFAQs />

                <div className='w-full'>
                  <AboutElite />
                </div>
              </div>
            )}
          </div>
        </div>

        {loadPricingRive && (
          <div
            style={{
              paddingBottom: `${getBottomPadding() + 8}px`,
              paddingTop: stateConfig?.supportText ? '4px' : '16px'
            }}
            className={`px-5 animate-fade-150 flex flex-col 
            gap-2 ${stateConfig?.supportText ? 'border-t-[1.5px] border-[#F6F9F9]' : ''} `}
          >
            {stateConfig?.supportText && (
              <SupportText
                eliteLandingPageState={eliteLandingPageState}
                activationExpiryDate={familyEliteDetails?.userSubscriptionDetails?.activationExpiryDate ?? undefined}
                onOpenBottomSheet={openBottomSheet}
                onCloseBottomSheet={closeBottomSheet}
              />
            )}
            <PrimaryButton
              onClick={handleContinue}
              label={stateConfig?.buttonText ?? 'Continue'}
              isDisabled={stateConfig?.isDisabled}
              primaryButtonClassName='!h-[56px] !text-[16px] !font-[500]'
            />
          </div>
        )}
      </div>
    </EliteBuyCtaExtrasProvider>
  )
}

export const EliteFallBackComponent = () => {
  return (
    <div
      style={{
        backgroundImage: 'url(/images/family/bg-hero.svg)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover'
      }}
      className='flex flex-col w-full h-full'
    >
      <div className='h-1 flex flex-1'></div>
      <div className='flex justify-center items-center'>
        <img src='/images/family/elite-icon.png' alt='Elite' className='w-[170px] h-[48px]' />
      </div>
    </div>
  )
}

export const PricingComponent = ({ price }: { price: number }) => {
  return (
    <div
      role={GROUPED_STATIC_TEXT_ROLE}
      aria-label={`₹${price ?? 0} per year, was ₹999 per year`}
      className='flex items-center gap-2 animate-fade-250'
    >
      <div aria-hidden='true' className='font-[700] text-[#CC5C2D] text-[24px] leading-[125%] tracking-[-1px] mb-1'>
        ₹{price}/yr
      </div>
      <div aria-hidden='true'>
        <StrikeThrough price='999' />
      </div>
    </div>
  )
}

export const StrikeThrough = ({ price }: { price: string }) => {
  return (
    <div className='relative flex flex-col items-center w-[85px]'>
      <span className='text-[#8E9393] text-[22px] font-[400] leading-[140%] select-none'>₹{price}/yr</span>
      <div className='absolute top-[17px] left-0 right-0 h-[2px] bg-[#8E9393] w-full -translate-y-1/2 pointer-events-none' />
      <div className='absolute top-[18px] h-[2px] bg-[#FEFAF3] w-[90%]' />
    </div>
  )
}

export const AboutElite = () => {
  const navigate = useNavigate()
  return (
    <div
      onClick={() => navigate('/elite/v5?pageType=no-upgrade')}
      className='flex justify-between items-center w-full rounded-xl p-4 border border-[#EBF2F2]'
    >
      <div className='text-[13px] font-[500] '>About PowerUp Elite</div>
      <div>
        <RenderHTML innerHTMLText={RightChevron} />
      </div>
    </div>
  )
}

export const RightChevron = `<svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
  <path d="M1 11L6 6L1 1" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`
